import { resolvePortalMessage } from "../../../i18n/message.js";
import { portalLocale, portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Funding — `/finance/funding` (plan-014 §4, design §27.15.1). Owned by plan-014 unit **D4**
 * (FR12-BIL-06, FR12-BIL-07, FR12-BIL-10; AC12-06).
 *
 * Drawn to the prototype's Finance page (`design/WeTalk.dc.html`, `data-screen-label="Organization"`, `funding`): kicker
 * "<legal payer> · legal payer", "Balances and funding", the Finance tab strip, then its four blocks —
 *
 *   stats      each balance, "Held for live calls — not spendable yet", "This month — across all workspaces"
 *   table      "Which balance pays for each workspace": Paid by, Since, Open usage — "A call keeps the balance it
 *              started on, even if you change funding while it is running."
 *   choice     "Change how <workspace> is funded": "Use the shared balance" / "Its own balance" — a server QUOTE first
 *              (the full effect: calls in progress, the balance that retires and keeps its credit, nothing charged),
 *              then Confirm; recovery status while an operation is still being applied
 *   form       "Move credit between balances": From (with what is available), To, Amount, Reason ("Shown on both
 *              ledgers") — quoted, then Move
 *
 * WHO SEES WHAT: the figures need `account.finance.read`; changing funding and moving credit need
 * `account.finance.manage` (Owner, Admin, Finance). What a role holds decides what the page OFFERS; the API decides.
 *
 * Every confirmation carries an Idempotency-Key derived from its quote, so a retry after a lost answer returns the same
 * operation and never makes a second one (FR12-BIL-08). plan-014 D3b's payment status block stays mounted for
 * `?operation=<id>` (the processor's return URL).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Field, Icon, Input, Select } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type { BillingAccountSummaryView } from "../../../api/slice/billing-account.js";
import type { BillingOperationStatusView, FundingQuoteView } from "../../../api/slice/billing-operation.js";
import { formatMoney } from "../../../money.js";
import { GLYPH } from "../../../shell/glyph.js";
import { roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { Block, BlockNote, FormPanel, StatGrid, type Outcome } from "../../account-admin/page/organization-page.js";
import { shortDateText } from "../../account-admin/page/role-copy.js";
import { PaymentStatusBlock } from "./payment-status.js";

import "./funding.css";
import { FinanceTabs } from "../finance-tabs.js";
import { talkTimeText } from "../../../talk-time.js";

/**
 * The page's search parameters. plan-014 D3b: `operation` — the billing operation whose payment status and recovery
 * the page shows (the return URL from the processor's page, and "Pay now" from a low-balance notice, land here).
 */
export interface FundingSearch {
  readonly operation?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL
 * still opens a valid page.
 */
export function fundingSearchOf(raw: Readonly<Record<string, unknown>>): FundingSearch {
  const operation = raw["operation"];
  return typeof operation === "string" && UUID.test(operation) ? { operation } : {};
}

export interface FundingScreenProps {
  readonly search: FundingSearch;
  readonly onSearch: (next: FundingSearch) => void;
}

export const fundingBalancesKey = (accountId: string) => ["billing-accounts", accountId] as const;

// ── the words and figures (pure; exported for the suite) ──────────────────────────────────────────────────

function eur(minor: number, currency: string): string {
  return formatMoney(money(minor, currency as CurrencyCode));
}

/** How a balance is named before Z2 gives it a display name. */
export function balanceNameOf(balance: Pick<BillingAccountSummaryView, "displayName" | "scope">): string {
  if (balance.displayName !== null) return balance.displayName;
  return balance.scope === "account" ? portalText("finance", "m0278") : portalText("finance", "m0517");
}

/** The stats row: each balance, then Held, then this month. */
export function fundingStatsOf(balances: readonly BillingAccountSummaryView[]): readonly { readonly label: string; readonly value: string; readonly note: string }[] {
  const currency = balances[0]?.currency ?? "EUR";
  const each = balances.map((one) => {
    const count = one.fundedWorkspace.length;
    let note: string;
    if (one.lifecycleState === "retiring") note = portalText("finance", "m0518");
    else if (one.scope === "workspace") note = portalText("finance", "funding.dedicated");
    else note = count === 1 ? portalText("finance", "m0519") : portalText("finance", "m0520", { value1: String(count) });
    return { label: balanceNameOf(one), value: eur(one.credit.balanceMinor, one.currency), note };
  });
  const held = balances.reduce((sum, one) => sum + one.credit.reservedMinor, 0);
  const month = balances.reduce((sum, one) => sum + one.monthSpendMinor, 0);
  return [
    ...each,
    { label: portalText("finance", "m0521"), value: eur(held, currency), note: portalText("finance", "m0522") },
    { label: portalText("finance", "m0182"), value: eur(month, currency), note: portalText("finance", "m0523") },
  ];
}

/** One row of "Which balance pays for each workspace". */
export interface FundingRow {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly billingAccountId: string;
  readonly paidBy: string;
  readonly since: string;
  readonly openSecond: number;
  readonly inProgress: number;
}

export function fundingRowsOf(balances: readonly BillingAccountSummaryView[]): readonly FundingRow[] {
  return balances
    .flatMap((balance) =>
      balance.fundedWorkspace.map((one) => ({
        workspaceId: one.workspaceId,
        workspaceName: one.workspaceName,
        billingAccountId: balance.billingAccountId,
        paidBy: balanceNameOf(balance),
        since: one.since,
        openSecond: one.unbilledTodaySecond + one.reservedSecond,
        inProgress: one.heldConversationCount,
      })),
    )
    .sort((left, right) => left.workspaceName.localeCompare(right.workspaceName));
}

/**
 * "4 min 10 s", "0 min 45 s", "—": open usage is time (the API's unit; money per workspace needs an allocation rule),
 * in minutes and seconds like all talk time (owner decision 2026-10-09). "—" when nothing is open.
 */
export function openUsageText(second: number): string {
  if (second <= 0) return "—";
  return talkTimeText(second);
}

/** "1 call in progress" — the prototype's copy for conversations in progress. */
function inProgressText(count: number): string {
  return count === 1 ? portalText("finance", "m0524") : portalText("finance", "m0525", { value1: String(count) });
}

/** "€100.00" / "100,5" → minor units, or `null` for anything that is not a positive amount with at most two decimals. */
export function amountMinorOf(text: string): number | null {
  const trimmed = text.trim().replace(/^€\s*/, "");
  const match = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(trimmed);
  if (match === null) return null;
  const whole = Number(match[1]);
  const cents = match[2] === undefined ? 0 : Number(match[2].padEnd(2, "0"));
  const minor = whole * 100 + cents;
  return minor > 0 ? minor : null;
}

/** The quote's effect, in the prototype's words ("Quote ready: from now, …"). */
export function fundingQuoteText(quote: FundingQuoteView): readonly string[] {
  const change = quote.funding;
  if (change === null) return [];
  const names = change.workspace.map((one) => one.workspaceName).join(", ");
  const lines = [portalText("finance", "m0526", { value1: names, value2: change.target.kind === "new_dedicated" ? portalText("finance", "funding.newBalance", { name: change.target.displayName }) : portalText("finance", "funding.targetBalance", { name: change.target.displayName }) })];
  for (const one of change.workspace) {
    if (one.heldConversationCount > 0) lines.push(portalText("finance", "m0527", { value1: inProgressText(one.heldConversationCount), value2: one.fromDisplayName }));
  }
  for (const one of change.retiring) {
    lines.push(portalText("finance", "m0528", { value1: one.displayName, value2: eur(one.balanceMinor, quote.currency) }));
  }
  lines.push(quote.amountDueMinor === 0 ? portalText("finance", "m0529") : portalText("finance", "m0530", { value1: eur(quote.amountDueMinor, quote.currency) }));
  return lines;
}

/** Idempotency-Key for a quote's confirmation: one quote, one key — a retry returns the same operation. */
export function confirmationKeyOf(quote: FundingQuoteView): string {
  return `${quote.kind === "transfer" ? "transfer" : "funding"}-${quote.billingQuoteId}`;
}

// ── the page frame ───────────────────────────────────────────────────────────────────────────────────────

function FinancePage(props: { readonly outcome: Outcome | null; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("finance");
  return (
    <section className="op-page">
      <h1 className="op-title">{portalText("finance", "m0531")}</h1>
      <p className="op-lede">{portalText("finance", "m0532")}</p>
      <FinanceTabs current="funding" />
      {props.outcome === null ? null : (
        <p className={props.outcome.tone === "done" ? "op-toast" : "op-toast op-toast-problem"} role="status">
          <Icon glyph={props.outcome.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="lg" />
          <span>{resolvePortalMessage(props.outcome.text)}</span>
        </p>
      )}
      {props.children}
    </section>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────────────

export function FundingScreen(props: FundingScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const balances = useQuery({
    queryKey: fundingBalancesKey(accountId ?? "none"),
    queryFn: () => gateway.listBillingAccounts(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const profile = useQuery({
    queryKey: ["billing-profile", accountId ?? "none"],
    queryFn: () => gateway.readBillingProfile(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const payer = profile.data === undefined || profile.data === null ? null : profile.data.legalName;

  const paymentBlock = props.search.operation === undefined ? null : <PaymentStatusBlock operationId={props.search.operation} />;

  if (open.loading || (canRead && balances.isPending && accountId !== null)) {
    return (
      <FinancePage outcome={null}>
        {paymentBlock}
        <BlockNote>{portalText("finance", "m0533")}</BlockNote>
      </FinancePage>
    );
  }
  if (accountId === null || !canRead) {
    return (
      <FinancePage outcome={null}>
        {paymentBlock}
        <BlockNote>{portalText("finance", "m0534")}</BlockNote>
      </FinancePage>
    );
  }
  if (balances.isError) {
    return (
      <FinancePage outcome={null}>
        {paymentBlock}
        <div className="fd-actions">
          <BlockNote>{apiProblemText(balances.error, portalText("finance", "m0535"))}</BlockNote>
          <Button variant="secondary" onClick={() => void balances.refetch()}>{portalText("finance", "m0382")}</Button>
        </div>
      </FinancePage>
    );
  }

  const balanceSet = balances.data ?? [];
  const rows = fundingRowsOf(balanceSet);
  return (
    <FinancePage outcome={outcome}>
      {paymentBlock}
      <Block label={portalText("finance", "m0536")}>
        <StatGrid stat={fundingStatsOf(balanceSet)} />
      </Block>
      <Block title={portalText("finance", "m0537")} dek={portalText("finance", "m0538")}>
        <div className="op-table-wrap">
          <table className="table op-table">
            <thead>
              <tr>
                <th>{portalText("finance", "m0389")}</th>
                <th>{portalText("finance", "m0539")}</th>
                <th>{portalText("finance", "m0540")}</th>
                <th className="fd-right">{portalText("finance", "m0541")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.workspaceId}>
                  <td>
                    <span className="op-cell-person">{row.workspaceName}</span>
                  </td>
                  <td>{row.paidBy}</td>
                  <td className="op-cell">{shortDateText(row.since)}</td>
                  <td className="op-cell fd-right">
                    {openUsageText(row.openSecond)}
                    {row.inProgress > 0 ? <span className="op-cell-sub">{inProgressText(row.inProgress)}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>
      {canManage && rows.length > 0 ? <FundingChangeBlock accountId={accountId} balances={balanceSet} rows={rows} onOutcome={setOutcome} /> : null}
      {canManage && balanceSet.length > 1 ? <MoveCreditBlock accountId={accountId} balances={balanceSet} payer={payer} onOutcome={setOutcome} /> : null}
    </FinancePage>
  );
}

// ── "Change how <workspace> is funded" ──────────────────────────────────────────────────────────────────────

type FundingChoice = { readonly kind: "keep" } | { readonly kind: "shared"; readonly billingAccountId: string } | { readonly kind: "own"; readonly billingAccountId: string | null };

interface ChoiceOption {
  readonly id: string;
  readonly label: string;
  readonly dek: string;
  readonly current: boolean;
  readonly choice: FundingChoice;
}

/** The choices for one workspace, in the prototype's words, with the real figures. */
export function fundingChoicesOf(row: FundingRow, balances: readonly BillingAccountSummaryView[]): readonly ChoiceOption[] {
  const shared = balances.find((one) => one.scope === "account");
  const current = balances.find((one) => one.billingAccountId === row.billingAccountId);
  const own = balances.find((one) => one.scope === "workspace" && one.fundedWorkspaceId === row.workspaceId && one.lifecycleState !== "retired");
  const onShared = current?.scope === "account";
  const options: ChoiceOption[] = [];
  if (shared !== undefined) {
    options.push({
      id: "shared",
      label: portalText("finance", "m0542"),
      dek: onShared
        ? portalText("finance", "m0543")
        : portalText("finance", "m0544", { value1: eur(current?.credit.balanceMinor ?? 0, current?.currency ?? shared.currency), value2: eur(shared.credit.balanceMinor, shared.currency) }),
      current: onShared,
      choice: onShared ? { kind: "keep" } : { kind: "shared", billingAccountId: shared.billingAccountId },
    });
  }
  options.push({
    id: "own",
    label: onShared ? (own === undefined ? portalText("finance", "m0545") : portalText("finance", "m0546")) : portalText("finance", "m0547"),
    dek: onShared
      ? own === undefined
        ? portalText("finance", "m0548")
        : portalText("finance", "m0549", { value1: eur(own.credit.balanceMinor, own.currency) })
      : portalText("finance", "m0550"),
    current: !onShared,
    choice: onShared ? { kind: "own", billingAccountId: own === undefined ? null : own.billingAccountId } : { kind: "keep" },
  });
  return options;
}

function FundingChangeBlock(props: {
  readonly accountId: string;
  readonly balances: readonly BillingAccountSummaryView[];
  readonly rows: readonly FundingRow[];
  readonly onOutcome: (outcome: Outcome) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const preferred = props.rows.find((one) => props.balances.find((balance) => balance.billingAccountId === one.billingAccountId)?.scope === "workspace") ?? props.rows[0];
  const [workspaceId, setWorkspaceId] = useState<string>(preferred?.workspaceId ?? "");
  const row = props.rows.find((one) => one.workspaceId === workspaceId) ?? preferred;
  const options = row === undefined ? [] : fundingChoicesOf(row, props.balances);
  const [picked, setPicked] = useState<string | null>(null);
  const chosen = options.find((one) => one.id === picked) ?? options.find((one) => one.current);
  const [quote, setQuote] = useState<FundingQuoteView | null>(null);
  const [pending, setPending] = useState<BillingOperationStatusView | null>(null);

  const quoting = useMutation({
    mutationFn: async () => {
      if (row === undefined || chosen === undefined || chosen.choice.kind === "keep") throw new Error(portalText("finance", "m0551"));
      const target = chosen.choice.kind === "own" && chosen.choice.billingAccountId === null ? { newDedicated: true as const } : { billingAccountId: chosen.choice.billingAccountId as string };
      return gateway.quoteBillingOperation(props.accountId, { kind: "funding_change", workspaceId: [row.workspaceId], target });
    },
    onSuccess: (answer) => setQuote(answer),
  });
  const confirming = useMutation({
    mutationFn: async (frozen: FundingQuoteView) => gateway.confirmBillingOperation(props.accountId, frozen.billingQuoteId, confirmationKeyOf(frozen)),
    onSuccess: async (operation, frozen) => {
      setQuote(null);
      setPicked(null);
      if (operation.state === "completed") {
        const name = frozen.funding?.workspace.map((one) => one.workspaceName).join(", ") ?? portalText("finance", "m0552");
        props.onOutcome({ tone: "done", text: () => (portalText("finance", "m0553", { value1: name, value2: frozen.funding?.target.displayName ?? portalText("finance", "m1258") })) });
        setPending(null);
      } else {
        setPending(operation);
      }
      await queryClient.invalidateQueries({ queryKey: ["billing-accounts"] });
    },
  });
  const checking = useMutation({
    mutationFn: (operationId: string) => gateway.readBillingOperation(operationId),
    onSuccess: async (operation) => {
      if (operation.state === "completed") {
        setPending(null);
        props.onOutcome({ tone: "done", text: () => (portalText("finance", "m0554")) });
        await queryClient.invalidateQueries({ queryKey: ["billing-accounts"] });
      } else {
        setPending(operation);
      }
    },
  });

  if (row === undefined) return null;
  const problem = quoting.isError ? quoting.error : confirming.isError ? confirming.error : checking.isError ? checking.error : null;
  return (
    <Block title={portalText("finance", "m0555", { value1: row.workspaceName })} dek={portalText("finance", "m0556")}>
      {props.rows.length > 1 ? (
        <div className="fd-pick">
          <Field label={portalText("finance", "m0389")}>
            {(control) => (
              <Select
                {...control}
                value={row.workspaceId}
                onChange={(event) => {
                  setWorkspaceId(event.target.value);
                  setPicked(null);
                  setQuote(null);
                }}
              >
                {props.rows.map((one) => (
                  <option key={one.workspaceId} value={one.workspaceId}>
                    {one.workspaceName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      ) : null}
      <div className="fd-choice" role="radiogroup" aria-label={portalText("finance", "m0557", { value1: row.workspaceName })}>
        {options.map((option) => (
          <label key={option.id} className={option.id === chosen?.id ? "fd-option fd-option-on" : "fd-option"}>
            <input
              type="radio"
              name={`funding-${row.workspaceId}`}
              className="fd-option-radio"
              checked={option.id === chosen?.id}
              onChange={() => {
                setPicked(option.id);
                setQuote(null);
              }}
            />
            <span className="fd-option-body">
              <span className="fd-option-label">
                {option.label}
                {option.current ? <span className="tag tag-accent fd-option-tag">{portalText("finance", "m0558")}</span> : null}
              </span>
              <span className="fd-option-dek">{option.dek}</span>
            </span>
          </label>
        ))}
      </div>

      {quote !== null && quote.funding !== null ? (
        <div className="fd-quote" role="status" aria-label={portalText("finance", "m0559")}>
          {fundingQuoteText(quote).map((line) => (
            <p key={line} className="fd-quote-line">
              {line}
            </p>
          ))}
          <p className="fd-quote-meta">{portalText("finance", "m0560")}{new Date(quote.expiresAt).toLocaleTimeString((portalLocale() === "el" ? "el-GR" : "en-GB"), { hour: "2-digit", minute: "2-digit" })}.</p>
        </div>
      ) : null}

      {pending !== null ? (
        <div className="fd-quote" role="status" aria-label={portalText("finance", "m0561")}>
          <p className="fd-quote-line">{portalText("finance", "m0562")}</p>
          <div className="fd-actions">
            <Button variant="secondary" disabled={checking.isPending} onClick={() => checking.mutate(pending.operationId)}>{portalText("finance", "m0563")}</Button>
          </div>
        </div>
      ) : null}

      {problem !== null ? (
        <p className="fd-error" role="alert">
          {apiProblemText(problem, portalText("finance", "m0564"))}
        </p>
      ) : null}

      <div className="fd-actions">
        {quote === null ? (
          <Button variant="primary" disabled={chosen === undefined || chosen.current || quoting.isPending} onClick={() => quoting.mutate()}>{portalText("finance", "m0565")}</Button>
        ) : (
          <>
            <Button variant="ghost" disabled={confirming.isPending} onClick={() => setQuote(null)}>{portalText("finance", "m0566")}</Button>
            <Button variant="primary" disabled={confirming.isPending} onClick={() => confirming.mutate(quote)}>{portalText("finance", "m0567")}</Button>
          </>
        )}
      </div>
    </Block>
  );
}

// ── "Move credit between balances" ──────────────────────────────────────────────────────────────────────────

function MoveCreditBlock(props: {
  readonly accountId: string;
  readonly balances: readonly BillingAccountSummaryView[];
  readonly payer: string | null;
  readonly onOutcome: (outcome: Outcome) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const sources = props.balances.filter((one) => one.lifecycleState !== "retired" && one.collectionPolicy === "prepaid");
  const [fromId, setFromId] = useState<string>(sources[0]?.billingAccountId ?? "");
  const destinations = props.balances.filter((one) => one.lifecycleState === "active" && one.billingAccountId !== fromId);
  const [toId, setToId] = useState<string>("");
  const to = destinations.find((one) => one.billingAccountId === toId) ?? destinations[0];
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [quote, setQuote] = useState<FundingQuoteView | null>(null);
  // plan-014 review I-21: the confirm answers 202 without the result; the move is reported from the operation as it
  // stands — "Moved" only once it completed and was not refused, otherwise a pending block with "Check again".
  const [pendingMove, setPendingMove] = useState<{ readonly operationId: string; readonly frozen: FundingQuoteView } | null>(null);
  const amountMinor = amountMinorOf(amount);

  const quoting = useMutation({
    mutationFn: async () => {
      if (to === undefined || amountMinor === null) throw new Error(portalText("finance", "m0568"));
      return gateway.quoteBillingOperation(props.accountId, {
        kind: "transfer",
        fromBillingAccountId: fromId,
        toBillingAccountId: to.billingAccountId,
        amountMinor,
        reason: reason.trim(),
      });
    },
    onSuccess: (answer) => setQuote(answer),
  });
  /** What the operation says now: refused, moved, or still being applied (then a pending block). */
  const report = async (operation: BillingOperationStatusView, frozen: FundingQuoteView): Promise<void> => {
    const move = frozen.transfer;
    const refused = operation.result?.["outcome"] === "refused";
    if (move !== null && refused) {
      setPendingMove(null);
      const could = operation.result?.["transferable_minor"];
      props.onOutcome({
        tone: "problem",
        text: () => (portalText("finance", "m0569", { value1: typeof could === "number" ? eur(Math.max(0, could), frozen.currency) : portalText("finance", "m1259") })),
      });
    } else if (move !== null && operation.state === "completed") {
      setPendingMove(null);
      props.onOutcome({
        tone: "done",
        text: () => (portalText("finance", "m0570", { value1: eur(move.amountMinor, frozen.currency), value2: move.from.displayName, value3: move.to.displayName })),
      });
      setAmount("");
      setReason("");
    } else if (operation.state === "failed" || operation.state === "cancelled") {
      setPendingMove(null);
      props.onOutcome({ tone: "problem", text: () => (portalText("finance", "m0571")) });
    } else {
      setPendingMove({ operationId: operation.operationId, frozen });
    }
    await queryClient.invalidateQueries({ queryKey: ["billing-accounts"] });
  };
  const moving = useMutation({
    mutationFn: async (frozen: FundingQuoteView) => {
      const confirmed = await gateway.confirmBillingOperation(props.accountId, frozen.billingQuoteId, confirmationKeyOf(frozen));
      // The confirm's answer carries no result (202): read the operation itself before saying anything moved.
      return confirmed.result === null ? gateway.readBillingOperation(confirmed.operationId) : confirmed;
    },
    onSuccess: async (operation, frozen) => {
      setQuote(null);
      await report(operation, frozen);
    },
  });
  const checkingMove = useMutation({
    mutationFn: (operationId: string) => gateway.readBillingOperation(operationId),
    onSuccess: async (operation) => {
      if (pendingMove !== null) await report(operation, pendingMove.frozen);
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (quote === null) quoting.mutate();
    else moving.mutate(quote);
  };
  const problem = quoting.isError ? quoting.error : moving.isError ? moving.error : checkingMove.isError ? checkingMove.error : null;
  const payer = props.payer === null ? portalText("finance", "m0572") : props.payer;
  return (
    <Block title={portalText("finance", "m0573")} dek={portalText("finance", "m0574", { value1: payer })}>
      <FormPanel onSubmit={submit} label={portalText("finance", "m0573")}>
        <Field label={portalText("finance", "m0575")}>
          {(control) => (
            <Select
              {...control}
              value={fromId}
              onChange={(event) => {
                setFromId(event.target.value);
                setToId("");
                setQuote(null);
              }}
            >
              {sources.map((one) => (
                <option key={one.billingAccountId} value={one.billingAccountId}>
                  {portalText("finance", "m0576", { value1: balanceNameOf(one), value2: eur(Math.max(0, one.credit.availableMinor), one.currency) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={portalText("finance", "m0577")}>
          {(control) => (
            <Select
              {...control}
              value={to?.billingAccountId ?? ""}
              onChange={(event) => {
                setToId(event.target.value);
                setQuote(null);
              }}
            >
              {destinations.map((one) => (
                <option key={one.billingAccountId} value={one.billingAccountId}>
                  {balanceNameOf(one)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={portalText("finance", "m0110")} {...(amount !== "" && amountMinor === null ? { error: portalText("finance", "m0578") } : {})}>
          {(control) => (
            <Input
              {...control}
              value={amount}
              inputMode="decimal"
              placeholder="€100.00"
              onChange={(event) => {
                setAmount(event.currentTarget.value);
                setQuote(null);
              }}
            />
          )}
        </Field>
        <Field label={portalText("finance", "m0579")} description={portalText("finance", "m0580")}>
          {(control) => (
            <Input
              {...control}
              value={reason}
              placeholder={portalText("finance", "m0581")}
              onChange={(event) => {
                setReason(event.currentTarget.value);
                setQuote(null);
              }}
            />
          )}
        </Field>
        {quote !== null && quote.transfer !== null ? (
          <div className="fd-quote fd-span" role="status" aria-label={portalText("finance", "m0582")}>
            <p className="fd-quote-line">
              {portalText("finance", "m0583", { value1: eur(quote.transfer.amountMinor, quote.currency), value2: quote.transfer.from.displayName, value3: quote.transfer.to.displayName, value4: quote.transfer.reason })}
            </p>
            <p className="fd-quote-meta">{portalText("finance", "m0584", { value1: eur(Math.max(0, quote.transfer.from.transferableMinor), quote.currency), value2: eur(quote.transfer.from.heldMinor, quote.currency) })}</p>
          </div>
        ) : null}
        {pendingMove !== null ? (
          <div className="fd-quote fd-span" role="status" aria-label={portalText("finance", "m0585")}>
            <p className="fd-quote-line">{portalText("finance", "m0586")}</p>
            <div className="fd-actions">
              <Button variant="secondary" type="button" disabled={checkingMove.isPending} onClick={() => checkingMove.mutate(pendingMove.operationId)}>{portalText("finance", "m0563")}</Button>
            </div>
          </div>
        ) : null}
        {problem !== null ? (
          <p className="fd-error fd-span" role="alert">
            {apiProblemText(problem, portalText("finance", "m0564"))}
          </p>
        ) : null}
        <div className="op-form-actions">
          {quote !== null ? (
            <Button variant="ghost" type="button" disabled={moving.isPending} onClick={() => setQuote(null)}>{portalText("finance", "m0566")}</Button>
          ) : null}
          <Button
            variant="primary"
            type="submit"
            disabled={to === undefined || amountMinor === null || reason.trim().length < 5 || quoting.isPending || moving.isPending}
          >
            {quote === null ? portalText("finance", "m0587") : portalText("finance", "m0588")}
          </Button>
        </div>
      </FormPanel>
    </Block>
  );
}
